/**
 * CV Service
 * Handles CV/Resume upload, storage, and management
 */

import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  getMetadata 
} from 'firebase/storage';
import { db, storage } from '../../../services/firebase/firebase';

/**
 * Upload CV file to Firebase Storage
 * 
 * @param {File} file - CV file
 * @param {string} userId - User ID
 * @param {function} onProgress - Progress callback
 * @returns {Promise<Object>} Upload result with URL and metadata
 */
export const uploadCV = async (file, userId, onProgress = null) => {
  try {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload PDF or Word document.');
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File size too large. Please upload a file smaller than 10MB.');
    }
    
    // Create storage reference
    const timestamp = Date.now();
    const fileName = `cv_${userId}_${timestamp}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `cvs/${userId}/${fileName}`);
    
    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(uploadResult.ref);
    const metadata = await getMetadata(uploadResult.ref);
    
    // Save CV metadata to Firestore
    const cvData = {
      userId,
      fileName: file.name,
      storagePath: uploadResult.ref.fullPath,
      downloadURL,
      fileSize: metadata.size,
      contentType: metadata.contentType,
      uploadedAt: serverTimestamp(),
      status: 'active'
    };
    
    const cvRef = doc(db, 'userCVs', `${userId}_${timestamp}`);
    await setDoc(cvRef, cvData);
    
    return {
      id: cvRef.id,
      ...cvData,
      downloadURL
    };
  } catch (error) {
    console.error('Error uploading CV:', error);
    throw error;
  }
};

/**
 * Get user's CV documents
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of CV documents
 */
export const getUserCVs = async (userId) => {
  try {
    const cvsQuery = query(
      collection(db, 'userCVs'),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );
    
    const snapshot = await getDocs(cvsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user CVs:', error);
    throw error;
  }
};

/**
 * Get CV by ID
 * 
 * @param {string} cvId - CV document ID
 * @returns {Promise<Object|null>} CV data or null if not found
 */
export const getCVById = async (cvId) => {
  try {
    const cvRef = doc(db, 'userCVs', cvId);
    const cvSnap = await getDoc(cvRef);
    
    if (cvSnap.exists()) {
      return {
        id: cvSnap.id,
        ...cvSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting CV by ID:', error);
    throw error;
  }
};

/**
 * Set primary CV for user
 * 
 * @param {string} userId - User ID
 * @param {string} cvId - CV document ID
 * @returns {Promise<void>}
 */
export const setPrimaryCV = async (userId, cvId) => {
  try {
    // First, remove primary status from all user's CVs
    const cvsQuery = query(
      collection(db, 'userCVs'),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(cvsQuery);
    const updatePromises = snapshot.docs.map(doc => 
      updateDoc(doc.ref, { isPrimary: false })
    );
    
    await Promise.all(updatePromises);
    
    // Set the selected CV as primary
    const cvRef = doc(db, 'userCVs', cvId);
    await updateDoc(cvRef, { 
      isPrimary: true,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error setting primary CV:', error);
    throw error;
  }
};

/**
 * Delete CV document
 * 
 * @param {string} cvId - CV document ID
 * @returns {Promise<void>}
 */
export const deleteCV = async (cvId) => {
  try {
    // Get CV data
    const cvData = await getCVById(cvId);
    if (!cvData) {
      throw new Error('CV not found');
    }
    
    // Delete from Firebase Storage
    const storageRef = ref(storage, cvData.storagePath);
    await deleteObject(storageRef);
    
    // Mark as deleted in Firestore (soft delete)
    const cvRef = doc(db, 'userCVs', cvId);
    await updateDoc(cvRef, {
      status: 'deleted',
      deletedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting CV:', error);
    throw error;
  }
};

/**
 * Get user's primary CV
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Primary CV data or null if not found
 */
export const getUserPrimaryCV = async (userId) => {
  try {
    const cvsQuery = query(
      collection(db, 'userCVs'),
      where('userId', '==', userId),
      where('isPrimary', '==', true),
      where('status', '==', 'active')
    );
    
    const snapshot = await getDocs(cvsQuery);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting primary CV:', error);
    throw error;
  }
};

/**
 * Update CV metadata
 * 
 * @param {string} cvId - CV document ID
 * @param {Object} metadata - Metadata to update
 * @returns {Promise<void>}
 */
export const updateCVMetadata = async (cvId, metadata) => {
  try {
    const cvRef = doc(db, 'userCVs', cvId);
    await updateDoc(cvRef, {
      ...metadata,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating CV metadata:', error);
    throw error;
  }
};

/**
 * Create CV parsing data (for future AI parsing implementation)
 * 
 * @param {string} cvId - CV document ID
 * @param {Object} parsedData - Parsed CV data
 * @returns {Promise<void>}
 */
export const saveParsedCVData = async (cvId, parsedData) => {
  try {
    const cvRef = doc(db, 'userCVs', cvId);
    await updateDoc(cvRef, {
      parsedData,
      isParsed: true,
      parsedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error saving parsed CV data:', error);
    throw error;
  }
};

/**
 * Get CV download URL with access control
 * 
 * @param {string} cvId - CV document ID
 * @param {string} requesterId - ID of user requesting access
 * @returns {Promise<string|null>} Download URL or null if access denied
 */
export const getCVDownloadURL = async (cvId, requesterId) => {
  try {
    const cvData = await getCVById(cvId);
    if (!cvData) {
      return null;
    }
    
    // Check if requester is the owner or has been granted access
    if (cvData.userId === requesterId || cvData.sharedWith?.includes(requesterId)) {
      return cvData.downloadURL;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting CV download URL:', error);
    throw error;
  }
};

/**
 * Share CV with specific users (for job applications)
 * 
 * @param {string} cvId - CV document ID
 * @param {Array} userIds - Array of user IDs to share with
 * @returns {Promise<void>}
 */
export const shareCVWithUsers = async (cvId, userIds) => {
  try {
    const cvRef = doc(db, 'userCVs', cvId);
    await updateDoc(cvRef, {
      sharedWith: userIds,
      sharedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error sharing CV:', error);
    throw error;
  }
};

export default {
  uploadCV,
  getUserCVs,
  getCVById,
  setPrimaryCV,
  deleteCV,
  getUserPrimaryCV,
  updateCVMetadata,
  saveParsedCVData,
  getCVDownloadURL,
  shareCVWithUsers
};